import { ConfigProvider } from 'antd';
import esES from 'antd/es/locale/es_ES';
import {
  primaryColor,
  warningColor,
  errorColor,
  successColor,
  infoColor,
  neutral,
  elevation,
  motion,
  fontStack,
} from './colors';

// Radios diferenciados:
// controles afilados (4px) — sensación de instrumento de precisión;
// contenedores suaves (12px) — descanso visual.
const controlRadius = 4;
const containerRadius = 12;

// whitTheme acepta `colors` opcional: { primary, secondary } para permitir
// que un tenant reescriba los tokens de marca (ver ThemedApp + useTenantColors).
// Si se omiten, cae a los defaults del theme (primaryColor, warningColor).
const whitTheme = (node, { primary, secondary } = {}) => {
  const p = primary ?? primaryColor;
  const s = secondary ?? warningColor;
  return (
    <>
      <ConfigProvider
        locale={esES}
        theme={{
          token: {
            // Paleta
            colorPrimary: p,
            colorInfo: infoColor,
            colorWarning: s,
            colorError: errorColor,
            colorSuccess: successColor,

            // Texto y fondos (slate frío, no blancos duros)
            colorText: neutral.text,
            colorTextSecondary: neutral.textMuted,
            colorTextTertiary: neutral.textFade,
            // Placeholder legible: el default de antd (~25% de opacidad, ~1.9:1
            // sobre blanco) es ilegible en móvil/exteriores. Lo subimos a
            // textFade (~4.5:1, pasa AA) sin confundirlo con texto real.
            colorTextPlaceholder: neutral.textFade,
            colorTextBase: neutral.ink,
            colorBgLayout: neutral.bg,
            colorBgContainer: neutral.surface,
            colorBgElevated: neutral.surfaceRaised,
            colorBorder: neutral.border,
            colorBorderSecondary: neutral.borderSoft,

            // Radios
            borderRadius: controlRadius,
            borderRadiusLG: containerRadius,
            borderRadiusSM: 2,
            borderRadiusXS: 2,
            borderRadiusOuter: controlRadius,

            // Tipografía — IBM Plex (herencia de ingeniería)
            fontFamily: fontStack.sans,
            fontFamilyCode: fontStack.mono,
            fontSize: 14,
            fontSizeLG: 15,
            fontSizeXL: 18,
            fontSizeHeading1: 30,
            fontSizeHeading2: 22,
            fontSizeHeading3: 18,
            fontSizeHeading4: 16,
            fontSizeHeading5: 14,
            lineHeight: 1.55,

            // Controles ligeramente más altos (tap-target + legibilidad)
            controlHeight: 36,
            controlHeightSM: 28,
            controlHeightLG: 44,

            // Motion snappy — instrumento mecánico, no gel
            motionDurationFast: motion.fast,
            motionDurationMid: motion.mid,
            motionDurationSlow: motion.slow,
            motionEaseInOut: motion.ease,
            motionEaseOut: motion.ease,

            // Sombras planas
            boxShadow: elevation.sh2,
            boxShadowSecondary: elevation.sh1,
            boxShadowTertiary: elevation.sh3,
          },
          components: {
            Layout: {
              headerBg: neutral.surface,
              triggerBg: p,
              siderBg: neutral.surface,
              bodyBg: neutral.bg,
            },
            Sider: {
              colorBgContainer: neutral.surface,
              colorTextBase: neutral.text,
              colorTextSecondary: neutral.textMuted,
              colorBorder: neutral.border,
              colorBgElevated: neutral.surface,
            },
            Menu: {
              itemColor: neutral.text,
              itemBorderRadius: controlRadius,
              itemSelectedBg: '#eef2f8',
              itemSelectedColor: p,
              itemBgHover: neutral.borderSoft,
              itemHoverColor: p,
              horizontalItemSelectedColor: p,
              fontWeight: 500,
            },
            Button: {
              borderRadius: controlRadius,
              fontWeight: 500,
              controlHeight: 36,
              defaultBorderColor: neutral.borderStrong,
              primaryShadow: 'none',
              defaultShadow: 'none',
              dangerShadow: 'none',
            },
            Input: {
              borderRadius: controlRadius,
              colorBgContainerDisabled: neutral.borderSoft,
              colorTextDisabled: neutral.textMuted,
              activeBorderColor: p,
              hoverBorderColor: p,
              paddingBlock: 8,
              paddingInline: 12,
            },
            InputNumber: {
              borderRadius: controlRadius,
              activeBorderColor: p,
            },
            Select: {
              borderRadius: controlRadius,
              colorBgContainerDisabled: neutral.borderSoft,
              colorTextDisabled: neutral.textMuted,
              optionSelectedBg: '#eef2f8',
              optionSelectedFontWeight: 500,
            },
            DatePicker: {
              borderRadius: controlRadius,
              activeBorderColor: p,
            },
            Table: {
              headerBg: neutral.borderSoft,
              headerColor: neutral.textMuted,
              headerSplitColor: 'transparent',
              borderColor: neutral.border,
              rowHoverBg: neutral.borderSoft,
              cellPaddingBlock: 12,
              cellPaddingInline: 14,
              fontWeightStrong: 500,
            },
            Tabs: {
              colorPrimary: p,
              // Barra activa en el color secundario del tenant (signature detail).
              inkBarColor: s,
              itemSelectedColor: neutral.ink,
              itemColor: neutral.textMuted,
              itemHoverColor: neutral.ink,
              titleFontSize: 14,
              horizontalMargin: '0 0 20px 0',
            },
            Tag: {
              defaultBg: neutral.borderSoft,
              defaultColor: neutral.text,
            },
            Drawer: {
              colorBgElevated: neutral.surface,
              colorBorder: neutral.border,
            },
            Modal: {
              colorBgElevated: neutral.surface,
              borderRadiusLG: containerRadius,
              paddingContentHorizontalLG: 24,
            },
            Card: {
              colorBgContainer: neutral.surface,
              colorBorderSecondary: neutral.border,
              headerFontSize: 16,
              headerHeight: 52,
              paddingLG: 20,
              boxShadowTertiary: elevation.sh1,
            },
            Empty: {
              colorTextDescription: neutral.textMuted,
            },
            Badge: {
              textFontSize: 11,
              statusSize: 7,
            },
            Statistic: {
              contentFontSize: 26,
              titleFontSize: 12,
            },
            Progress: {
              defaultColor: p,
              remainingColor: neutral.borderSoft,
            },
            Alert: {
              colorInfoBg: '#e9eef7',
              colorSuccessBg: '#e4f0e7',
              colorWarningBg: '#faeadb',
              colorErrorBg: '#f5d8d3',
              borderRadiusLG: controlRadius,
            },
            Divider: {
              colorSplit: neutral.border,
            },
            Form: {
              labelFontSize: 13,
              labelColor: neutral.text,
              verticalLabelPadding: '0 0 4px',
            },
            Segmented: {
              itemActiveBg: neutral.surface,
              itemSelectedBg: neutral.surface,
              itemSelectedColor: neutral.ink,
            },
            Tooltip: {
              colorBgSpotlight: neutral.ink,
              colorTextLightSolid: '#fff',
              borderRadius: controlRadius,
              paddingXS: 8,
            },
          },
        }}
      >
        {node}
      </ConfigProvider>
    </>
  );
};

export default whitTheme;
